import BaseLayer from './BaseLayer';
import { portPolskaAttribution } from './attribution';
import { setVisibility } from '../utils/maplibre-helpers';
import { PORT_POLSKA_AREAS_PATH, PORT_POLSKA_KIND_STYLES, portPolskaLayerDefinitions } from './port-polska-styles';

export { PORT_POLSKA_AREAS_PATH, PORT_POLSKA_KIND_STYLES };

/**
 * Port Polska reservation plans and investment bans imported into Metroskop.
 * The whole set is a single cached GeoJSON (about 100 kB compressed), loaded
 * once per page and revalidated by ETag, so it can stay visible from zoom 8
 * without per-viewport requests.
 */
export default class PortPolskaLayer extends BaseLayer {
    static ID = 'portPolska';
    static DESCRIPTION = 'Plany rezerwacji terenu i zakazy inwestycyjne Port Polska';
    static MIN_ZOOM = 8;

    constructor(config = {}) {
        super(config);
        this.data = null;
        this.request = null;
    }

    getFillLayerId() { return this.id + '-fill'; }
    getLineLayerId() { return this.id + '-line'; }
    getLayerIds() { return [this.getFillLayerId(), this.getLineLayerId()]; }

    fetchAreas() {
        if (this.data) return Promise.resolve(this.data);
        if (!this.request) {
            const baseUrl = (this.config.baseUrl || '/').replace(/\/?$/, '/');
            this.request = fetch(baseUrl + PORT_POLSKA_AREAS_PATH, { credentials: 'same-origin' })
                .then(response => {
                    if (!response.ok) throw new Error('Port Polska areas unavailable: HTTP ' + response.status);
                    return response.json();
                })
                .then(data => { this.data = data; return data; })
                .finally(() => { this.request = null; });
        }
        return this.request;
    }

    load() {
        if (!this.canActivate() || !this.isDesired()) {
            this.clear();
            return Promise.resolve();
        }
        const requestStarted = this.config.onRequestStarted;
        const requestSettled = this.config.onRequestSettled;
        const tracked = !this.data && !!requestStarted;
        if (tracked) requestStarted(this);
        return this.fetchAreas().then(data => {
            if (!this.isActive() || !this.isDesired()) return;
            this.render(data);
        }).catch(() => {
            // A failed download leaves the map usable; the next toggle retries.
            this.setActive(false);
            this.updateToggleState();
        }).finally(() => {
            if (tracked && requestSettled) requestSettled(this);
        });
    }

    render(data) {
        const map = this.getMap();
        if (!map) return;
        const sourceId = this.getSourceId();
        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, { type: 'geojson', data, attribution: portPolskaAttribution() });
            portPolskaLayerDefinitions(sourceId, this.id, this.minZoom).forEach(layer => map.addLayer(layer));
        }
        this.getLayerIds().concat(this.getLineLayerId() + '-research').forEach(id => setVisibility(map, id, true));
        if (this.config.onLoaded) this.config.onLoaded();
    }

    removeLayerFromMap() {
        const map = this.getMap();
        if (!map) return;
        [this.getLineLayerId() + '-research', ...this.getLayerIds()].forEach(id => { if (map.getLayer(id)) map.removeLayer(id); });
        if (map.getSource(this.getSourceId())) map.removeSource(this.getSourceId());
    }

    clear() {
        this.removeLayerFromMap();
        if (this.config.onCleared) this.config.onCleared();
    }
}
